import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { setTitle, setMeta, setCanonical } from "@/lib/seo";
import { Loader2, ArrowRight, Newspaper } from "lucide-react";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const title = "Blog | Saving America Money";
    const desc = "Local marketing tips, postcard advertising guides, and small-business growth ideas from Saving America Money.";
    setTitle(title);
    setMeta("description", desc);
    setMeta("og:title", title, "property");
    setMeta("og:description", desc, "property");
    setMeta("og:type", "website", "property");
    setCanonical(`${window.location.origin}/blog`);
    api.get("/blog/posts").then(({ data }) => setPosts(data.posts || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="mx-auto max-w-5xl px-6 py-14" data-testid="blog-page">
      <div className="flex items-center gap-2 text-sam-crimson text-[11px] font-bold uppercase tracking-[0.2em]">
        <Newspaper className="h-4 w-4" /> SAM Journal
      </div>
      <h1 className="mt-3 font-display font-extrabold text-sam-navy text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
        Grow smarter, <span className="text-sam-crimson">spend less.</span>
      </h1>
      <p className="mt-4 text-sam-slate max-w-2xl text-base">
        Playbooks on local advertising, the 9×12 postcard mailer, and getting found by customers in your hometown.
      </p>

      {loading ? (
        <div className="py-24 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sam-crimson" /></div>
      ) : posts.length === 0 ? (
        <div className="mt-12 rounded-2xl border border-dashed border-sam-navy/20 bg-sam-cream/50 p-12 text-center" data-testid="blog-empty">
          <p className="text-sam-slate">No articles yet, check back soon for fresh local-marketing guides.</p>
        </div>
      ) : (
        <div className="mt-12 grid gap-6 sm:grid-cols-2">
          {posts.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              data-testid={`blog-card-${p.slug}`}
              className="group rounded-2xl border border-border bg-card p-6 transition-all hover:border-sam-crimson/40 hover:shadow-[0_12px_40px_-18px_rgba(10,25,47,0.3)]"
            >
              <div className="text-[11px] font-semibold uppercase tracking-wider text-sam-slate">
                {(p.created_at || "").slice(0, 10)}
              </div>
              <h2 className="mt-2 font-display font-bold text-sam-navy text-lg leading-snug group-hover:text-sam-crimson transition-colors">
                {p.title}
              </h2>
              <p className="mt-2 text-sm text-sam-slate line-clamp-3">{p.meta_description || p.excerpt}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-sam-crimson group-hover:gap-2.5 transition-all">
                Read article <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
